import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import Box from '@mui/system/Box';
import { WarningAlert } from '../Alert/WarningAlert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { apiAuth, EMfaMethod } from '../../resources/api/apiAuth';
import { MFADevicesFetcher } from '../../fetchers/MfaDevicesFetcher';
import { MFAMethodsFetcher } from '../../fetchers/MfaMethodsFetcher';
import { DisableDeviceButton } from './DisableDeviceButton';
import { UUID } from '../../types/uuid';

const randomNameAdjective = [
  'unhinged',
  'zany',
  'goofy',
  'daft',
  'loopy',
  'distinguished',
  'elegant',
  'stubborn',
  'outstanding',
  'gargantuan',
];
const randomNameNoun = [
  'unicorn',
  'toad',
  'elf',
  'acromantula',
  'ghoul',
  'gnome',
  'basilisk',
  'hippogriff',
  'jarvey',
  'goblin',
];

const getRandomName = () => {
  return (
    randomNameAdjective[Math.floor(Math.random() * randomNameAdjective.length)] +
    ' ' +
    randomNameNoun[Math.floor(Math.random() * randomNameNoun.length)]
  );
};

const { useRegister2faByMethodId } = apiAuth;

export const TemporaryAuthAppSettings: FC<{ setDeviceId: (id: UUID) => void; setMfaSent: () => void }> = ({
  setDeviceId,
  setMfaSent,
}) => {
  const [qr, setQr] = useState('');
  const { execute: register2faByMethodId } = useRegister2faByMethodId();

  const { data: mfaDevices, onRefresh: onMfaRefresh } = useContext(MFADevicesFetcher.Context);
  const { data: mfaMethodsData } = useContext(MFAMethodsFetcher.Context);

  const onRegisterAuthApp = useCallback(
    async (is_default: boolean) => {
      if (!appAuth) {
        alert('auth app not available');
        return;
      }
      const registerResult = await register2faByMethodId({
        is_default,
        name: getRandomName(),
        method_id: appAuth.id,
      });
      setDeviceId(registerResult.payload.device_id);
      setQr(registerResult.payload.qr);
      await onMfaRefresh();
    },
    [register2faByMethodId]
  );

  const appAuth = mfaMethodsData?.find((method) => method.name === EMfaMethod.AuthApp);
  const appDevices = useMemo(
    () => (mfaDevices ?? []).filter((device) => device.method_name === EMfaMethod.AuthApp),
    [mfaDevices]
  );

  return (
    <Box mt={2}>
      <WarningAlert>
        <p>THIS IS ONLY FOR TESTING PURPOSES AND NEEDS TO BE REMOVED BEFORE THE RELEASE</p>

        <Table>
          <TableBody>
            {appDevices.map((device) => (
              <TableRow key={device.device_id}>
                <TableCell>
                  {device.device_id} &quot;{device.device_name}&quot;
                </TableCell>
                <TableCell>
                  <DisableDeviceButton device={device} onRefresh={onMfaRefresh}>
                    Disable 2FA via Auth App
                  </DisableDeviceButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div>
          <Button variant="contained" onClick={() => onRegisterAuthApp(false)}>
            Register Auth App
          </Button>{' '}
          <Button variant="contained" color="secondary" onClick={() => onRegisterAuthApp(true)}>
            Register Auth App (default)
          </Button>
        </div>
      </WarningAlert>
      <Dialog
        open={!!qr}
        onClose={() => {
          setQr('');
          setMfaSent();
        }}
      >
        <DialogContent>
          <img src={`data:image/png;base64,${qr}`} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
